import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const rotate = keyframes`
  to { transform: rotate(360deg); }
`;

const dash = keyframes`
  0%   { stroke-dasharray: 2 150;  stroke-dashoffset: 0; }
  50%  { stroke-dasharray: 90 150; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90 150; stroke-dashoffset: -124; }
`;

const slide = keyframes`
  0%   { transform: translateX(120%); }
  100% { transform: translateX(-220%); }
`;

const Wrapper = styled.div`
  min-height: calc(100vh - 180px);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-family: IRANSans, Tahoma, sans-serif;

  /* با کمی تأخیر ظاهر می‌شود تا لودهای خیلی کوتاه چشمک نزنند */
  opacity: 0;
  animation: ${fadeUp} 0.4s ease-out 0.3s forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const SpinnerBox = styled.div`
  width: 36px;
  height: 36px;
  margin-bottom: 8px;
`;

const Ring = styled.svg`
  width: 100%;
  height: 100%;
  animation: ${rotate} 1.6s linear infinite;

  .track {
    fill: none;
    stroke: #e3e9ef;
    stroke-width: 3.5;
  }
  .arc {
    fill: none;
    stroke: #27ae60;
    stroke-width: 3.5;
    stroke-linecap: round;
    animation: ${dash} 1.6s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 3s;
    .arc { animation: none; stroke-dasharray: 60 150; }
  }
`;

const Title = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #10263b;
  user-select: none;
`;

const Sub = styled.p`
  margin: 0;
  font-size: 11px;
  color: #8a9aab;
  user-select: none;
`;

const Progress = styled.div`
  position: relative;
  width: 80px;
  height: 2px;
  margin-top: 10px;
  border-radius: 2px;
  background: #e3e9ef;
  overflow: hidden;

  span {
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline-start: 0;
    width: 40%;
    border-radius: 2px;
    background: #27ae60;
    animation: ${slide} 1.4s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    span { animation: none; width: 100%; opacity: 0.5; }
  }
`;

interface SpinnerProps {
  title?: string;
  subtitle?: string;
}

const Spinner = React.memo(
  ({ title = 'در حال بارگذاری', subtitle = 'لطفاً چند لحظه صبر کنید' }: SpinnerProps) => (
    <Wrapper className="sanjabSpin" dir="rtl" role="status" aria-live="polite">
      <Content>
        <SpinnerBox aria-hidden="true">
          <Ring viewBox="0 0 50 50">
            <circle className="track" cx="25" cy="25" r="20" />
            <circle className="arc" cx="25" cy="25" r="20" />
          </Ring>
        </SpinnerBox>

        <Title>{title}</Title>
        <Sub>{subtitle}</Sub>

        <Progress aria-hidden="true">
          <span />
        </Progress>
      </Content>
    </Wrapper>
  )
);

export default Spinner;